import React from 'react'
import { useNavigate } from 'react-router-dom'
import {
	CheckIcon,
	BoltIcon,
	SparklesIcon,
} from '@heroicons/react/24/solid'
import LoadFetcherLogo from '../../media/loadfetcher-logo.svg'
import useGlobalTheme from '../../hooks/useGlobalTheme'

const Pricing = () => {
	const navigate = useNavigate()
	const theme = useGlobalTheme()
	const isLight = theme === 'light'

	const plans = [
		{
			name: 'Free',
			price: 0,
			description: 'Perfect for getting started',
			icon: BoltIcon,
			iconColor: 'text-blue-400',
			buttonStyle:
				'from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800',
			features: [
				'1 linked email account',
				'1 reusable email template',
				'Core filters (distance · rate · state)',
				'Built-in Google Maps',
				'Email statistics dashboard',
				'Email CC',
				'Email history',
				'Reply email notifications',
			],
			buttonText: 'Get Started Free',
		},
		{
			name: 'Plus',
			price: 14.99,
			description: 'Full power for serious carriers',
			icon: SparklesIcon,
			iconColor: 'text-amber-400',
			buttonStyle:
				'from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600',
			includesText: 'Everything in Free, plus:',
			features: [
				'Auto Reach - vet, filter & auto email',
				'Up to 3 email accounts',
				'10 saved templates',
				'Custom Maps with toll info',
				'Custom Maps with weather info',
				'Broker risk assessment',
				'Broker contact vetting',
				'Factoring score analysis',
			],
			buttonText: 'Start Plus Plan',
			subText: '7-day free trial · cancel anytime',
		},
	]

	const handlePlanSelection = () => {
		navigate('/register')
	}

	const handleLoadFetcherConnect = () => {
		navigate('/register')
	}

	return (
		<div
			id="pricing"
			className={`bg-gradient-to-b pt-0 pb-12 scroll-mt-20 ${isLight ? 'bg-white text-gray-900' : 'bg-[#070c11] text-[#eef3f9]'}`}
		>
			<div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 w-full pricing-container">
				<div className="px-6 sm:px-7 lg:px-7 pricing-inner-wrapper">
					<div className={`rounded-b-3xl mx-auto max-w-7xl -mt-1 ${isLight ? 'bg-white' : 'bg-[#070c11]'}`}>
						<div className="py-8 px-12 pricing-content-box">
							<div className="text-center mb-12 pricing-header">
								<h2 className={`text-5xl font-extrabold mb-8 leading-tight pricing-title ${isLight ? 'text-gray-900' : 'text-white'}`}>
									Simple, Transparent
									<span className={`px-4 py-2 mx-2 rounded-xl transform rotate-1 inline-block pricing-title-highlight ${isLight ? 'bg-sky-100 text-sky-900' : 'bg-[#003d5c] text-white'}`}>
										Pricing
									</span>
								</h2>

								{/* LoadFetcher Banner */}
								<div className="max-w-3xl mx-auto mb-8">
									<div className={`rounded-2xl p-6 border ${isLight ? 'bg-gradient-to-r from-sky-50 to-sky-100 border-sky-200' : 'bg-gradient-to-r from-[#003456] to-[#004a77] border-[#005a8c]/50'}`}>
										<div className="flex flex-col sm:flex-row items-center justify-between gap-4">
											<div className="flex items-center gap-3">
												<div className="text-left">
													<p className={`font-semibold text-lg ${isLight ? 'text-gray-900' : 'text-white'}`}>
														Already a LoadFetcher subscriber?
													</p>
													<p className={`text-sm ${isLight ? 'text-gray-600' : 'text-[#b3cde4]'}`}>
														LoadLine is included free with your LoadFetcher subscription.
													</p>
												</div>
											</div>
											<button
												onClick={handleLoadFetcherConnect}
												className={`px-6 py-3 rounded-full font-semibold transition-colors whitespace-nowrap flex items-center ${isLight ? 'bg-white text-gray-800 border border-gray-300 hover:bg-gray-50' : 'bg-white hover:bg-gray-100 text-gray-800'}`}
											>
												Connect&nbsp;<img src={LoadFetcherLogo} alt="" className="w-6 h-6" /><span className="text-gray-500">Load</span><span className="text-[#2463eb]">Fetcher</span>&nbsp;→
											</button>
										</div>
									</div>
								</div>

								{/* Divider */}
								<div className="flex items-center justify-center gap-4 mb-8">
									<div className={`h-px flex-1 max-w-[200px] ${isLight ? 'bg-gray-200' : 'bg-[#1a3a52]'}`}></div>
									<span className={`text-sm font-medium ${isLight ? 'text-gray-600' : 'text-[#b3cde4]'}`}>
										Or subscribe to LoadLine only
									</span>
									<div className={`h-px flex-1 max-w-[200px] ${isLight ? 'bg-gray-200' : 'bg-[#1a3a52]'}`}></div>
								</div>
							</div>

							<div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto pricing-grid">
								{plans.map(plan => {
									const IconComponent = plan.icon
									return (
										<div
											key={plan.name}
											className={`rounded-3xl shadow-2xl p-6 relative pricing-card ${isLight ? 'bg-white ring-1 ring-gray-200 shadow-lg' : 'bg-[#003456]'}`}
										>

											<div className="text-center mb-6 pricing-card-header">
												<h3 className={`text-2xl font-bold mb-2 pricing-card-title flex items-center justify-center gap-3 ${isLight ? 'text-gray-900' : 'text-white'}`}>
													<div className={`inline-flex items-center justify-center w-10 h-10 rounded-full pricing-card-icon ${isLight ? 'bg-sky-50' : 'bg-[#002340]'}`}>
														<IconComponent
															className={`w-6 h-6 ${plan.iconColor}`}
														/>
													</div>
													{plan.name}
												</h3>
												<p className={`mb-4 pricing-card-description ${isLight ? 'text-gray-600' : 'text-[#b3cde4]'}`}>
													{plan.description}
												</p>
												<div className="mb-4 pricing-price-container">
													<div className="flex items-center justify-center">
														<span className={`text-5xl font-extrabold pricing-current-price ${isLight ? 'text-gray-900' : 'text-white'}`}>
															{plan.price === 0 ? 'Free' : `$${plan.price}`}
														</span>
														{plan.price > 0 && (
															<span className={`text-lg pricing-price-period ${isLight ? 'text-gray-600' : 'text-[#b3cde4]'}`}>
																/month
															</span>
														)}
													</div>
												</div>
											</div>

											{plan.includesText && (
												<p className={`text-sm font-medium mb-3 ${isLight ? 'text-emerald-600' : 'text-emerald-400'}`}>
													{plan.includesText}
												</p>
											)}
											<ul className="space-y-3 mb-6 pricing-features-list">
												{plan.features.map((feature, index) => (
													<li
														key={index}
														className="flex items-center gap-3 pricing-feature-item"
													>
														<CheckIcon className={`w-5 h-5 flex-shrink-0 pricing-feature-icon ${isLight ? 'text-emerald-600' : 'text-emerald-400'}`} />
														<span className={`pricing-feature-text ${isLight ? 'text-gray-700' : 'text-[#b3cde4]'}`}>
															{feature}
														</span>
													</li>
												))}
											</ul>

											<button
												onClick={handlePlanSelection}
												className={`w-full text-white px-6 py-3 rounded-full text-lg font-bold shadow-xl bg-gradient-to-r pricing-cta-button ${plan.buttonStyle}`}
											>
												{plan.buttonText}
											</button>

											{plan.subText && (
												<p className={`text-sm mt-4 text-center pricing-subtext ${isLight ? 'text-gray-600' : 'text-[#b3cde4]'}`}>
													{plan.subText}
												</p>
											)}
										</div>
									)
								})}
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	)
}

export default Pricing
